import { useState, useMemo } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, fmtEuro, capitalizeName } from './lib/format.js';
import { Modal, Avatar } from './components.jsx';
import { MentionTextarea, MentionText } from './mention-input.jsx';
import { IconX, IconCheck } from './icons.jsx';

const MIN_REASON = 150;

// Smart closure modal. Three render paths based on the project state:
//
//  A) project.status === 'active' && in budget
//     → Single-step success close. Confetti on confirm.
//
//  B) project.status === 'active' && over budget
//     → Beteiligte (oder Admin) starten den Abschluss-Prozess. Projekt geht
//        auf 'closing'. Danach muss jede beteiligte Person ihre Begründung
//        eintragen, bevor abgeschlossen werden kann.
//
//  C) project.status === 'closing'
//     → "Workflow in progress" view. Current user (if involved) can
//        write/edit their own reason. Other users' reasons appear as
//        completed/pending badges. Sobald alle Begründungen vorliegen, kann
//        jede beteiligte Person endgültig abschließen.
//
// Abschließen darf, wer am Projekt beteiligt ist (Team oder erfasste Zeit),
// es erstellt hat, oder Admin ist — durchgesetzt wird das von der RPC
// `close_project` (Migration 25), nicht nur hier im UI.
function ProjectClosureModal({ project, onClose, onConfetti, pushToast }) {
  const {
    profiles, currentProfile, isAdmin,
    projectUsage, involvedByProject, completionsByProject,
    upsertCompletion, startProjectClosing, finalizeProject, cancelProjectClosing, syncMentions,
  } = useData();

  const usage = projectUsage[project.id] || { minutes: 0, euro: 0 };
  const overBudget = !!project.budget_minutes && usage.minutes > project.budget_minutes;
  const overshootPct = project.budget_minutes
    ? Math.round((usage.minutes / project.budget_minutes - 1) * 100)
    : 0;
  const inBudget = !overBudget;
  const isClosing = project.status === 'closing';

  // Resolve involved users
  const involvedIds = involvedByProject[project.id] || [];
  const involvedUsers = involvedIds
    .map(id => profiles.find(p => p.id === id))
    .filter(Boolean);

  const completionsHere = completionsByProject[project.id] || [];
  const completionByUser = useMemo(() => {
    const m = {};
    for (const c of completionsHere) m[c.user_id] = c;
    return m;
  }, [completionsHere]);

  const myCompletion = completionByUser[currentProfile.id];
  const iAmInvolved = involvedIds.includes(currentProfile.id);
  // Wer den Abschluss auslösen darf — dieselbe Regel wie in der RPC.
  const canClose = isAdmin || iAmInvolved || project.created_by === currentProfile.id;
  const allReasonsIn = involvedUsers.length > 0 &&
                       involvedUsers.every(u => !!completionByUser[u.id]);
  const missingUsers = involvedUsers.filter(u => !completionByUser[u.id]);

  const [reason, setReason] = useState(myCompletion?.reason || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reasonValid = reason.trim().length >= MIN_REASON;

  // Die RPC wirft sprechende Kürzel — hier werden sie zu Klartext.
  const RPC_ERRORS = {
    not_allowed: 'Nur Beteiligte, der Ersteller oder ein Admin können dieses Projekt abschließen.',
    reasons_missing: 'Es fehlen noch Begründungen von Beteiligten.',
    closing_not_started: 'Bei überzogenem Budget muss zuerst der Abschluss-Prozess gestartet werden.',
    invalid_status: 'Der Projektstatus passt nicht zu dieser Aktion — lade die Seite neu.',
    project_not_found: 'Projekt nicht gefunden.',
  };
  function humanError(e) {
    const raw = e?.message || '';
    const key = Object.keys(RPC_ERRORS).find(k => raw.includes(k));
    return key ? RPC_ERRORS[key] : (raw || 'Fehler');
  }

  async function handleStartClosing() {
    setBusy(true); setErr(null);
    try {
      await startProjectClosing(project.id);
      pushToast?.({ emoji: '⚠️', title: 'Abschluss gestartet', body: 'Beteiligte müssen jetzt ihre Begründung eintragen.' });
      // Don't close — switch view to closing flow
    } catch (e) {
      setErr(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveReason() {
    if (!reasonValid || busy) return;
    setBusy(true); setErr(null);
    try {
      await upsertCompletion({ project_id: project.id, reason: reason.trim() });
      await syncMentions({
        text: reason,
        prevText: myCompletion?.reason || '',
        sourceType: 'project_closure',
        sourceId: project.id,
        projectId: project.id,
      });
      pushToast?.({ emoji: '💾', title: 'Begründung gespeichert' });
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
    } finally {
      setBusy(false);
    }
  }

  // Admins kommen immer durch (RPC-Regel aus Migration 26). Damit das nicht
  // versehentlich passiert, fragen wir vorher nach und nennen die fehlenden
  // Personen beim Namen.
  async function handleAdminForceFinalize() {
    const names = missingUsers.map(u => capitalizeName(u.full_name) || u.email).join(', ');
    const msg = names
      ? `Ohne die Begründung von ${names} abschließen?\n\nDas Projekt wird als „Abgeschlossen · überzogen" markiert; die fehlenden Begründungen bleiben offen sichtbar.`
      : 'Projekt mit Budgetüberzug abschließen?';
    if (!window.confirm(msg)) return;
    await handleFinalize(true);
  }

  async function handleFinalize(wasOverBudget) {
    setBusy(true); setErr(null);
    try {
      // Ob wirklich überzogen wurde, entscheidet die RPC anhand der erfassten
      // Zeiten; `wasOverBudget` steuert hier nur Konfetti und Toast-Text.
      await finalizeProject(project.id);
      if (!wasOverBudget) onConfetti?.();
      pushToast?.({
        emoji: wasOverBudget ? '✅' : '🎉',
        title: wasOverBudget ? 'Mit Überziehung abgeschlossen' : 'Erfolgreich abgeschlossen!',
        body: wasOverBudget ? 'Alle Begründungen wurden erfasst.' : 'Im Budget geblieben — top.',
      });
      onClose();
    } catch (e) {
      setErr(humanError(e));
      setBusy(false);
    }
  }

  async function handleCancelClosing() {
    if (!window.confirm('Abschluss abbrechen? Eingetragene Begründungen bleiben erhalten, das Projekt geht zurück auf „aktiv".')) return;
    setBusy(true);
    try {
      await cancelProjectClosing(project.id);
      pushToast?.({ emoji: '↩️', title: 'Zurück auf aktiv', body: 'Begründungen bleiben gespeichert.' });
      onClose();
    } catch (e) {
      setErr(humanError(e));
      setBusy(false);
    }
  }

  // ---- Render ---------------------------------------------------------------
  return (
    <Modal onClose={busy ? () => {} : onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div className="eyebrow">Projekt abschließen</div>
          <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
            {project.title}
          </h2>
          {project.budget_minutes && (
            <div className="tiny muted mono" style={{ marginTop: 6 }}>
              {fmtMin(usage.minutes)} / {fmtMin(project.budget_minutes)}
              {project.budget_euro && <> · {fmtEuro(usage.euro)} / {fmtEuro(project.budget_euro)}</>}
              {overBudget && <span style={{ color: 'var(--crit)', marginLeft: 8 }}>+{overshootPct}%</span>}
            </div>
          )}
        </div>
        <button className="icon-btn" onClick={onClose} disabled={busy}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
        {/* Path A: in-budget, ready to confirm */}
        {!isClosing && inBudget && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 12 }}>🎉</div>
            <h3 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: 0, color: 'var(--ok)' }}>
              Im Budget abgeschlossen
            </h3>
            <p className="muted" style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.5 }}>
              {project.budget_minutes
                ? `${fmtMin(project.budget_minutes - usage.minutes)} unter dem geplanten Budget. Ein guter Tag.`
                : 'Bereit zum Abschluss.'}
            </p>
            {canClose ? (
              <button className="btn btn-primary" onClick={() => handleFinalize(false)} disabled={busy}
                      style={{ marginTop: 18, padding: '10px 20px' }}>
                {busy ? 'Schließe ab…' : 'Erfolgreich abschließen'}
              </button>
            ) : (
              <div className="tiny muted" style={{ marginTop: 18 }}>
                Abschließen können Beteiligte, der Ersteller oder ein Admin.
              </div>
            )}
          </div>
        )}

        {/* Path B: active + over-budget — admin must start the closing process */}
        {!isClosing && overBudget && (
          <div>
            <div style={{
              padding: 14, borderRadius: 10,
              background: 'var(--crit-soft)', border: '1px solid rgba(239,68,68,0.25)',
              color: 'var(--crit)', fontSize: 13.5, fontWeight: 500,
              display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16,
            }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 600 }}>Budget überzogen um {overshootPct}%</div>
                <div className="tiny" style={{ color: 'var(--crit)', opacity: 0.9, marginTop: 4 }}>
                  Bevor das Projekt abgeschlossen wird, muss jeder Beteiligte eine Begründung von mindestens {MIN_REASON} Zeichen erfassen. Die Begründungen sieht das Team und die Geschäftsführung.
                </div>
              </div>
            </div>

            <div className="eyebrow" style={{ marginBottom: 8 }}>Beteiligte ({involvedUsers.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {involvedUsers.length === 0 && (
                <div className="tiny muted">Keine Mitglieder zugeordnet und keine Zeit erfasst — du kannst trotzdem abschließen.</div>
              )}
              {involvedUsers.map(u => (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 8, background: 'var(--bg-sunken)',
                  border: '1px solid var(--stroke)',
                }}>
                  <Avatar user={u} size={26}/>
                  <div style={{ fontSize: 13 }}>{capitalizeName(u.full_name) || u.email}</div>
                </div>
              ))}
            </div>

            {canClose ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={handleStartClosing} disabled={busy}>
                  {busy ? 'Starte…' : 'Abschluss-Prozess starten'}
                </button>
                {/* Aufräum-Weg für die Geschäftsführung: alte überzogene Projekte
                    schließen, ohne für jedes den Begründungs-Prozess zu starten. */}
                {isAdmin && (
                  <button className="btn" onClick={handleAdminForceFinalize} disabled={busy}>
                    Direkt abschließen
                  </button>
                )}
              </div>
            ) : (
              <div className="tiny muted">
                Den Abschluss starten können Beteiligte, der Ersteller oder ein Admin.
              </div>
            )}
          </div>
        )}

        {/* Path C: closing in progress */}
        {isClosing && (
          <div>
            <div style={{
              padding: 14, borderRadius: 10,
              background: 'var(--warn-soft)', border: '1px solid rgba(245,158,11,0.3)',
              color: 'var(--warn)', fontSize: 13.5, fontWeight: 500,
              display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 18,
            }}>
              <span style={{ fontSize: 16 }}>📝</span>
              <div>
                <div style={{ fontWeight: 600 }}>
                  Abschluss läuft — {Object.keys(completionByUser).length} von {involvedUsers.length} Begründungen erfasst
                </div>
                <div className="tiny" style={{ color: 'var(--warn)', opacity: 0.9, marginTop: 4 }}>
                  Sobald alle Beteiligten ihre Begründung eingetragen haben, kann das Projekt endgültig abgeschlossen werden.
                  {isAdmin && !allReasonsIn && ' Als Admin kannst du auch jetzt schon abschließen — fehlende Begründungen bleiben sichtbar.'}
                </div>
              </div>
            </div>

            {/* My reason input (only if I'm involved) */}
            {iAmInvolved && (
              <div style={{ marginBottom: 22 }}>
                <div className="eyebrow" style={{ marginBottom: 8 }}>
                  Deine Begründung {myCompletion ? '· bereits gespeichert' : '· erforderlich'}
                </div>
                <MentionTextarea
                  value={reason}
                  onChange={setReason}
                  profiles={profiles}
                  placeholder={`Was hat dazu geführt, dass die geplante Zeit überzogen wurde? Mindestens ${MIN_REASON} Zeichen.`}
                  style={{ minHeight: 120 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <div className="tiny" style={{ color: reasonValid ? 'var(--ok)' : 'var(--ink-3)' }}>
                    {reason.trim().length} / {MIN_REASON} Zeichen {reasonValid && '✓'}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveReason}
                    disabled={!reasonValid || busy || (myCompletion && reason.trim() === myCompletion.reason)}
                    style={{ opacity: (reasonValid && !busy && (!myCompletion || reason.trim() !== myCompletion.reason)) ? 1 : 0.45 }}
                  >
                    {busy ? 'Speichere…' : myCompletion ? 'Aktualisieren' : 'Begründung speichern'}
                  </button>
                </div>
              </div>
            )}

            {/* Status of all involved members */}
            <div className="eyebrow" style={{ marginBottom: 8 }}>Begründungen aller Beteiligten</div>
            <div className="tiny muted" style={{ marginBottom: 8 }}>
              Sichtbar für das Team und in der Auswertung für die Geschäftsführung.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {involvedUsers.map(u => {
                const c = completionByUser[u.id];
                const isMe = u.id === currentProfile.id;
                return (
                  <div key={u.id} style={{
                    padding: 12, borderRadius: 10,
                    background: 'var(--bg-sunken)',
                    border: c ? '1px solid rgba(16,185,129,0.25)' : '1px solid var(--stroke)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar user={u} size={24}/>
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                        {capitalizeName(u.full_name) || u.email}{isMe && <span className="tiny muted" style={{ marginLeft: 6 }}>(du)</span>}
                      </div>
                      {c ? (
                        <span className="chip chip-ok" style={{ height: 22 }}>
                          <IconCheck size={11}/> erfasst
                        </span>
                      ) : (
                        <span className="chip chip-warn" style={{ height: 22 }}>ausstehend</span>
                      )}
                    </div>
                    {c && !isMe && (
                      <div className="tiny" style={{
                        marginTop: 8, color: 'var(--ink-2)', lineHeight: 1.5,
                        padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 6,
                      }}>
                        <MentionText text={c.reason}/>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Abschluss-Aktionen — für Beteiligte, Ersteller und Admins */}
            {canClose && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', borderTop: '1px solid var(--stroke)', paddingTop: 14 }}>
                <button className="btn btn-ghost" onClick={handleCancelClosing} disabled={busy}>
                  Abschluss abbrechen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => (allReasonsIn ? handleFinalize(true) : handleAdminForceFinalize())}
                  disabled={busy || (!allReasonsIn && !isAdmin)}
                  style={{ opacity: (busy || (!allReasonsIn && !isAdmin)) ? 0.45 : 1 }}
                  title={
                    allReasonsIn ? 'Alle Begründungen liegen vor'
                      : isAdmin ? 'Als Admin auch ohne alle Begründungen möglich'
                      : 'Es fehlen noch Begründungen'
                  }
                >
                  {busy ? 'Schließe ab…'
                    : allReasonsIn ? 'Endgültig abschließen'
                    : 'Ohne alle Begründungen abschließen'}
                </button>
              </div>
            )}
          </div>
        )}

        {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{err}</div>}
      </div>
    </Modal>
  );
}

export { ProjectClosureModal };
